import { type ApiError, adminApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * Driver-licence review panel rendered inside the admin booking detail page.
 * Pickup is gated by the customer's most-recent licence being `valid`, so the operator needs
 * to be able to approve / reject the uploaded photo from inside the booking workflow.
 *
 * Shows the customer's licences newest-first. Each row has a thumbnail preview + status pill
 * + approve / reject controls when actionable.
 */
export function LicencePanel({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'customer', customerId, 'licences'],
    queryFn: () => adminApi.customerLicences(customerId),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'customer', customerId, 'licences'] });
  };

  const items = data?.items ?? [];
  const latest = items[0];

  return (
    <div
      style={{
        marginTop: 20,
        paddingTop: 16,
        borderTop: '1px solid var(--lv-line)',
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}
      >
        <div className="label" style={{ color: 'var(--lv-brand)' }}>
          CARTA DE CONDUÇÃO
        </div>
        <span style={{ fontSize: 11, color: 'var(--lv-gray-500)' }}>
          {items.length === 0 ? '' : `${items.length} upload(s)`}
        </span>
      </div>

      {isLoading ? (
        <p style={{ fontSize: 12, color: 'var(--lv-gray-500)' }}>A carregar…</p>
      ) : items.length === 0 ? (
        <p
          style={{
            fontSize: 12,
            color: 'var(--lv-gray-500)',
            padding: 16,
            border: '1px dashed var(--lv-line)',
            textAlign: 'center',
            background: '#faf6f6',
          }}
        >
          O cliente ainda não submeteu nenhuma carta de condução. O levantamento está bloqueado até existir
          uma carta válida — peça ao cliente para a carregar em /conta/documentos.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
          {items.map((row) => (
            <LicenceRowItem key={row.id} row={row} latest={row.id === latest?.id} onChange={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}

interface RowProps {
  row: {
    id: string;
    status: 'pending' | 'valid' | 'rejected' | 'expired';
    rejectionReason: string | null;
    reviewedAt: string | null;
    expiry: string | null;
    mimeType: string;
    bytes: number;
    createdAt: string;
    signedUrl: string;
  };
  latest: boolean;
  onChange: () => void;
}

function LicenceRowItem({ row, latest, onChange }: RowProps) {
  const [expiry, setExpiry] = useState<string>(row.expiry ?? '');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const approve = useMutation({
    mutationFn: () => adminApi.approveLicence(row.id, { expiry: expiry || undefined }),
    onSuccess: onChange,
  });
  const reject = useMutation({
    mutationFn: () => adminApi.rejectLicence(row.id, { reason: rejectReason }),
    onSuccess: () => {
      setShowReject(false);
      setRejectReason('');
      onChange();
    },
  });

  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short', timeStyle: 'short' });

  return (
    <li
      style={{
        border: '1px solid var(--lv-line)',
        background: latest ? 'var(--lv-surface-soft)' : '#fff',
        padding: 12,
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gap: 14,
      }}
    >
      <a
        href={row.signedUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          width: 120,
          height: 78,
          background: '#000',
          overflow: 'hidden',
          border: '1px solid var(--lv-line)',
        }}
      >
        {row.mimeType.startsWith('image/') ? (
          <img
            src={row.signedUrl}
            alt="Carta de condução"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div
            style={{
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              padding: 10,
              textAlign: 'center',
            }}
          >
            PDF · {(row.bytes / 1024).toFixed(0)} kB
          </div>
        )}
      </a>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <StatusBadge status={row.status} />
          {latest && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: 'var(--lv-brand)',
              }}
            >
              · MAIS RECENTE
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--lv-gray-500)' }}>
            Carregada em {dateFmt.format(new Date(row.createdAt))}
          </span>
        </div>

        {row.rejectionReason && (
          <div style={{ fontSize: 12, color: 'var(--lv-err)' }}>
            <strong>Motivo:</strong> {row.rejectionReason}
          </div>
        )}
        {row.expiry && (
          <div style={{ fontSize: 12, color: 'var(--lv-gray-500)' }}>
            Validade registada: <strong>{row.expiry}</strong>
          </div>
        )}

        {row.status === 'pending' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--lv-gray-500)',
                }}
              >
                Validade (opcional)
              </span>
              <input
                type="date"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                style={{
                  padding: '6px 8px',
                  border: '1px solid var(--lv-line)',
                  fontSize: 12,
                  width: 150,
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => approve.mutate()}
              disabled={approve.isPending}
              style={btnApprove}
            >
              {approve.isPending ? '…' : 'Aprovar'}
            </button>
            <button type="button" onClick={() => setShowReject((v) => !v)} style={btnRejectGhost}>
              {showReject ? 'Cancelar' : 'Rejeitar'}
            </button>
          </div>
        )}

        {showReject && row.status === 'pending' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (rejectReason.trim().length >= 3) reject.mutate();
            }}
            style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'flex-end' }}
          >
            <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--lv-gray-500)',
                }}
              >
                Motivo da rejeição
              </span>
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="ex: foto desfocada, carta expirada"
                required
                minLength={3}
                maxLength={500}
                style={{
                  padding: '6px 8px',
                  border: '1px solid var(--lv-line)',
                  fontSize: 12,
                  width: '100%',
                }}
              />
            </label>
            <button type="submit" disabled={reject.isPending} style={btnReject}>
              {reject.isPending ? '…' : 'Confirmar rejeição'}
            </button>
          </form>
        )}

        {(approve.error || reject.error) && (
          <div style={{ fontSize: 11, color: 'var(--lv-err)' }}>
            {(approve.error as ApiError | undefined)?.message ??
              (reject.error as ApiError | undefined)?.message}
          </div>
        )}
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: 'pending' | 'valid' | 'rejected' | 'expired' }) {
  const label =
    status === 'valid'
      ? 'VÁLIDA'
      : status === 'pending'
        ? 'PENDENTE'
        : status === 'rejected'
          ? 'REJEITADA'
          : 'EXPIRADA';
  const bg =
    status === 'valid'
      ? 'rgba(45,122,79,0.12)'
      : status === 'pending'
        ? 'rgba(200,134,15,0.12)'
        : 'rgba(185,28,28,0.12)';
  const color =
    status === 'valid' ? 'var(--lv-ok)' : status === 'pending' ? 'var(--lv-warn)' : 'var(--lv-err)';
  return (
    <span
      style={{
        background: bg,
        color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        padding: '3px 8px',
      }}
    >
      {label}
    </span>
  );
}

const btnApprove: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '8px 14px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnReject: React.CSSProperties = {
  background: 'var(--lv-err)',
  color: '#fff',
  border: 0,
  padding: '8px 14px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnRejectGhost: React.CSSProperties = {
  background: '#fff',
  color: 'var(--lv-err)',
  border: '1px solid var(--lv-err)',
  padding: '8px 14px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
